import { Bot, KeyRound, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@/shared/components/empty-state';
import { ListCard } from '@/shared/components/list/list-card';
import { ListSkeleton } from '@/shared/components/list/list-skeleton';
import { useTranslation } from '@/shared/i18n/use-translation';
import { decideAccessRequest } from '../api/decide-access-request';
import { revokeSession } from '../api/revoke-session';
import { describeLoadError } from '../helpers/describe-load-error';
import { normalizeUserCode } from '../helpers/normalize-user-code';
import { splitSessions } from '../helpers/split-sessions';
import { useAccessRequests } from '../hooks/use-access-requests';
import { useSessions } from '../hooks/use-sessions';
import { AccessRequestRow } from './access-request-row';
import { SessionRow } from './session-row';

type AccessScreenProps = {
  /** From `?code=`: the request an agent sent the person here to approve. */
  readonly code: string | null;
};

export function AccessScreen({ code }: AccessScreenProps) {
  const { t } = useTranslation();
  const requests = useAccessRequests();
  const sessions = useSessions();
  const wanted = normalizeUserCode(code);
  const pending = requests.data ?? [];
  const missing =
    wanted !== null && requests.data !== undefined && !pending.some((r) => r.user_code === wanted);
  const { devices, delegated } = splitSessions(sessions.data ?? []);

  async function decide(id: string, name: string, decision: 'approve' | 'deny') {
    try {
      await decideAccessRequest(id, decision);
      toast.success(decision === 'approve' ? t('{name} now has access', { name }) : t('Request denied'));
    } catch {
      toast.error(t('The request is no longer pending (it may have expired)'));
    }
    await Promise.all([requests.reload(), sessions.reload()]);
  }

  async function revoke(id: string) {
    try {
      await revokeSession(id);
      toast.success(t('Access revoked'));
    } catch {
      toast.error(t('It could not be revoked. Try again.'));
    }
    await sessions.reload();
  }

  if (requests.error && sessions.error) {
    return <EmptyState icon={WifiOff} title={t('No data')} description={describeLoadError(requests.error)} />;
  }

  return (
    <>
      <section className="flex flex-col gap-3" aria-labelledby="access-requests">
        <h2 id="access-requests" className="font-heading text-base font-medium">
          {t('Access requests')}
        </h2>
        {missing && (
          <p className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
            {t(
              'There is no pending request with the code {code}. It may have expired (they last 10 minutes): ask the agent to start another one.',
              { code: wanted },
            )}
          </p>
        )}
        {requests.loading ? (
          <ListSkeleton rows={1} />
        ) : pending.length === 0 ? (
          <EmptyState
            icon={Bot}
            title={t('No pending requests')}
            description={t(
              'An agent (Claude, a script…) can ask for access by following {url}. Its request will show up here with a code.',
              { url: `${window.location.origin}/llms.txt` },
            )}
          />
        ) : (
          <ListCard>
            {pending.map((request) => (
              <AccessRequestRow
                key={request.id}
                request={request}
                highlighted={request.user_code === wanted}
                onDecide={(decision) => decide(request.id, request.client_name, decision)}
              />
            ))}
          </ListCard>
        )}
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="delegated-sessions">
        <h2 id="delegated-sessions" className="font-heading text-base font-medium">
          {t('Agents with access')}
        </h2>
        {sessions.loading ? (
          <ListSkeleton rows={1} />
        ) : delegated.length === 0 ? (
          <EmptyState icon={KeyRound} title={t('No agent has access')} />
        ) : (
          <ListCard>
            {delegated.map((session) => (
              <SessionRow key={session.id} session={session} onRevoke={() => revoke(session.id)} />
            ))}
          </ListCard>
        )}
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="device-sessions">
        <h2 id="device-sessions" className="font-heading text-base font-medium">
          {t('Your devices')}
        </h2>
        {sessions.loading ? (
          <ListSkeleton rows={2} />
        ) : (
          <ListCard>
            {devices.map((session) => (
              <SessionRow key={session.id} session={session} onRevoke={() => revoke(session.id)} />
            ))}
          </ListCard>
        )}
      </section>
    </>
  );
}
